import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { Capacitor } from '@capacitor/core';
import { EvictableDataStore, PermanentDataStore } from 'libs/storage';
import { ENV } from 'shared/ENV';
import { AuthState } from 'shared/types';

export type LocalStorageKey =
  (typeof LOCAL_STORAGE_KEY)[keyof typeof LOCAL_STORAGE_KEY];

export const LOCAL_STORAGE_KEY = {
  AUTH_STATE: 'AUTH_STATE',
  DEVICE_INFO: 'DEVICE_INFO',
  PUSH_TOKEN: 'PUSH_TOKEN',
  MAP_STATE: 'MAP_STATE',
  MAP_LAYERS: 'MAP_LAYERS',
  MAP_LAYERS_PROGRESSION_MODEL: 'MAP_LAYERS_PROGRESSION_MODEL',
  MAP_BASE_LAYER: 'MAP_BASE_LAYER',
  MAP_VIEW_DIMENSION: 'MAP_VIEW_DIMENSION',
  MAP_LAYERS_DRAWER: 'MAP_LAYERS_DRAWER',
  LAST_KNOWN_GEOLOCATION: 'LAST_KNOWN_GEOLOCATION',
  ONBOARDING_STATE: 'ONBOARDING_STATE',
  APP_INFO: 'APP_INFO',
  MAP_PLACES: 'MAP_PLACES',
  PLACES_SEARCHBAR: 'PLACES_SEARCHBAR',
  MEMBERSHIP_STATE: 'MEMBERSHIP_STATE',
  ORGANIZATION_STATE: 'ORGANIZATION_STATE',
  HAS_WEB_GL_2: 'HAS_WEB_GL_2',
  INBOX_LAST_FETCHED_DATE: 'INBOX_LAST_FETCHED_DATE',
  LAST_PUSH_NOTIFICATION_TIMESTAMP: 'LAST_PUSH_NOTIFICATION_TIMESTAMP',
  IAP_STATE: 'IAP_STATE',
  DONATION_STATE: 'DONATION_STATE',
  CACHE: 'CACHE',
  SUPPORT_US_BANNER_AD: 'SUPPORT_US_BANNER_AD',
  SYNC_APP_PURCHASE: 'SYNC_APP_PURCHASE',
  LOCKED_MAP_PIN: 'LOCKED_MAP_PIN',
  COORDINATE_FORMAT: 'COORDINATE_FORMAT',
  PHOTO_SUBMISSION_DRAFT: 'PHOTO_SUBMISSION_DRAFT',
};

export const SESSION_STORAGE_ACCESSKEY_KEY = 'ACCESSKEY';

export const SESSION_STORAGE_KEY = {
  MAPBOX_SEARCH_SESSION_TOKEN: 'MAPBOX_SEARCH_SESSION_TOKEN',
};

export const LOGOUT_KEEP_LOCAL_STORAGE_KEYS = [
  LOCAL_STORAGE_KEY.ONBOARDING_STATE,
];

/**
 * @linear:ENG-2541 This will be removed in favor of
 * PermanentDataStore.getItem()/EvictableDataStore.getItem() as per RFC-0011.
 */
export const getData = <T>(key: string, defaultValue?: T): T | undefined => {
  try {
    const data = localStorage.getItem(key);
    if (data === null) {
      return defaultValue;
    }
    return JSON.parse(data);
  } catch (err) {
    return defaultValue;
  }
};

/**
 * @linear:ENG-2541 This will be removed in favor of
 * PermanentDataStore.setItem()/EvictableDataStore.setItem() as per RFC-0011.
 */
export const saveData = <T>(key: string, data: T): null => {
  try {
    const serializedData = JSON.stringify(data);

    if (!data) {
      localStorage.removeItem(key);
      return null;
    }

    localStorage.setItem(key, serializedData);
  } catch (err) {
    return null;
  }
  return null;
};

/**
 * Get the user's access key / auth token. This will look for a cached access key
 * for efficiency in sessionStorage and otherwise pull it out of AUTH_STATE.
 */
export const getAccessKey = async (): Promise<string | null> => {
  const sessionStorageValue = sessionStorage.getItem(
    SESSION_STORAGE_ACCESSKEY_KEY,
  );

  if (sessionStorageValue) {
    return sessionStorageValue;
  }

  const authState = ENV.VITE_USE_NEW_STORAGE
    ? await PermanentDataStore.getItem<AuthState>('AUTH_STATE')
    : getData<AuthState>(LOCAL_STORAGE_KEY.AUTH_STATE);

  const token = authState?.accessKey || null;

  if (token) {
    sessionStorage.setItem(SESSION_STORAGE_ACCESSKEY_KEY, token);
  }

  return token;
};

/*
  Push Token Get & Set
*/
export const savePushTokenToLocalStorage = (token: string | null): void => {
  saveData(LOCAL_STORAGE_KEY.PUSH_TOKEN, token || null);
};

export const getPushTokenFromLocalStorage = (): string | null => {
  const token = getData<string>(LOCAL_STORAGE_KEY.PUSH_TOKEN);
  if (token) {
    return token;
  }

  // required for saucelabs ios devices to bypass push registration
  // fake token is needed since resigned app does not support this
  if (ENV.VITE_MOCK_PUSH_TOKEN && Capacitor.getPlatform() === 'ios') {
    // Generate once and save to localStorage so it persists for this device
    const uniqueToken = `saucelabs-device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    savePushTokenToLocalStorage(uniqueToken);
    return uniqueToken;
  }

  // As developers we need a hack to develop in the browser
  // this way you can pass in `push_token` as a requst param
  // and it will get saved to localStorage
  const params = new URL(document.location.toString()).searchParams;
  const devPushToken = params.get('push_token');
  if (devPushToken) {
    savePushTokenToLocalStorage(devPushToken);
    return devPushToken;
  }

  if (!token) {
    return null;
  }
  return token;
};

/**
 * Clear Data
 */
export const clearDataForLogout = async (): Promise<void> => {
  sessionStorage.clear();

  // First, clear all in-memory queries to ensure no stale data remains
  persistedQueryClient.clear();
  // Then, remove the persisted cache from localStorage
  localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');

  const toRemove = Object.values(LOCAL_STORAGE_KEY).filter(
    (key) => !LOGOUT_KEEP_LOCAL_STORAGE_KEYS.includes(key),
  );
  toRemove.forEach((key) => {
    localStorage.removeItem(key);
  });

  if (ENV.VITE_USE_NEW_STORAGE) {
    await PermanentDataStore.clear();
    await EvictableDataStore.clear();
  }

  // clear denormalized auth token left behind by older apps (pre-April 2025)
  localStorage.removeItem('ACCESSKEY');
};

export const setLastPushNotificationTimestamp = (): void => {
  saveData(LOCAL_STORAGE_KEY.LAST_PUSH_NOTIFICATION_TIMESTAMP, {
    timestamp: Date.now(),
  });
};

export const getLastPushNotificationTimestamp = (): number => {
  return (
    getData<{ timestamp: number }>(
      LOCAL_STORAGE_KEY.LAST_PUSH_NOTIFICATION_TIMESTAMP,
    )?.timestamp || 0
  );
};
